<template>
  <section class="checkout-payment-embed checkout-payment-embed--stripe">
    <div class="checkout-payment-embed__hero">
      <div>
        <span class="checkout-payment-embed__label">Pago con tarjeta</span>
        <h3 class="checkout-payment-embed__title">Ingresa los datos de tu tarjeta para cerrar esta orden.</h3>
        <p class="checkout-payment-embed__copy">Si tu banco pide validacion adicional, la hacemos aqui mismo sin sacarte del checkout.</p>
      </div>

      <div class="checkout-payment-embed__chips">
        <span>Solo tarjeta</span>
        <span>Sin redireccion</span>
        <span>3DS aqui</span>
      </div>
    </div>

    <div v-if="errorMessage" class="workspace-feedback workspace-feedback--error">{{ errorMessage }}</div>
    <div v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</div>

    <div v-show="loading" class="checkout-payment-embed__loading">
      <span class="checkout-payment-embed__loading-ring"></span>
      <div>
        <strong>Preparando formulario seguro...</strong>
        <small>Estamos montando la tarjeta dentro de FOMO.</small>
      </div>
    </div>

    <div ref="paymentElement" class="checkout-payment-embed__mount"></div>
    <p class="checkout-payment-embed__hint">Total a confirmar: <strong>{{ formattedAmount }}</strong></p>

    <div class="workspace-actions workspace-actions--end checkout-payment-embed__actions">
      <button class="btn btn-primary liquid-page-button checkout-payment-embed__button" type="button" :disabled="loading || confirming || !ready" @click="confirmPayment">
        <span class="liquid-page-button__glow"></span>
        <span class="liquid-page-button__ring"></span>
        <span class="liquid-page-button__inner">
          <span class="liquid-page-button__copy">{{ confirming ? 'Confirmando...' : `Pagar ${formattedAmount}` }}</span>
          <span class="liquid-page-button__icon"><i class="fas fa-lock"></i></span>
        </span>
      </button>
    </div>
  </section>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js'
import { formatCurrencyAmount, normalizeCurrencyCode } from '../../common/currency.js'

export default {
  name: 'StripePaymentElement',
  props: {
    attemptToken: {
      type: String,
      required: true,
    },
    clientSecret: {
      type: String,
      required: true,
    },
    publishableKey: {
      type: String,
      required: true,
    },
    amount: {
      type: [Number, String],
      default: 0,
    },
    currency: {
      type: String,
      default: 'USD',
    },
  },
  data() {
    return {
      loading: false,
      confirming: false,
      ready: false,
      stripe: null,
      elements: null,
      paymentElement: null,
      errorMessage: '',
      successMessage: '',
    }
  },
  computed: {
    formattedAmount() {
      return formatCurrencyAmount(this.amount, normalizeCurrencyCode(this.currency, 'USD'))
    },
  },
  mounted() {
    this.initialize()
  },
  beforeUnmount() {
    if (this.paymentElement) {
      this.paymentElement.unmount()
      this.paymentElement = null
    }
    this.elements = null
    this.stripe = null
  },
  methods: {
    async initialize() {
      this.loading = true
      this.errorMessage = ''
      if (!this.publishableKey || !this.clientSecret) {
        this.loading = false
        this.errorMessage = 'Faltan credenciales publicas de Stripe o client secret.'
        return
      }

      this.stripe = await loadStripe(this.publishableKey)
      if (!this.stripe) {
        this.loading = false
        this.errorMessage = 'Stripe no pudo cargarse en este navegador.'
        return
      }

      this.elements = this.stripe.elements({
        clientSecret: this.clientSecret,
        appearance: {
          theme: 'night',
          variables: {
            colorPrimary: '#f4f7fb',
            colorBackground: '#0d1017',
            colorText: '#edf2ff',
            colorDanger: '#ff7b9c',
            colorTextSecondary: '#aeb7ca',
            borderRadius: '16px',
            spacingUnit: '6px',
            fontFamily: 'Space Grotesk, sans-serif',
          },
          rules: {
            '.Block': {
              backgroundColor: 'rgba(7, 10, 17, 0.92)',
              boxShadow: 'none',
            },
            '.Input': {
              backgroundColor: 'rgba(255, 255, 255, 0.035)',
              border: '1px solid rgba(198, 205, 220, 0.12)',
              boxShadow: 'none',
            },
            '.Input:focus': {
              border: '1px solid rgba(244, 247, 251, 0.28)',
              boxShadow: '0 0 0 1px rgba(244, 247, 251, 0.08)',
            },
            '.Label': {
              color: '#dbe4f4',
              fontWeight: '600',
            },
            '.AccordionItem': {
              border: '1px solid rgba(198, 205, 220, 0.1)',
              backgroundColor: 'rgba(255, 255, 255, 0.025)',
              boxShadow: 'none',
            },
            '.CodeInput': {
              backgroundColor: 'rgba(255, 255, 255, 0.035)',
              border: '1px solid rgba(198, 205, 220, 0.12)',
              boxShadow: 'none',
            },
          },
        },
      })
      this.paymentElement = this.elements.create('payment', {
        layout: {
          type: 'accordion',
          defaultCollapsed: false,
          radios: 'never',
          spacedAccordionItems: false,
        },
        paymentMethodOrder: ['card'],
        wallets: {
          applePay: 'never',
          googlePay: 'never',
        },
      })
      this.paymentElement.mount(this.$refs.paymentElement)
      this.ready = true
      this.loading = false
    },
    async confirmPayment() {
      if (!this.stripe || !this.elements) {
        return
      }
      this.confirming = true
      this.errorMessage = ''
      const { error, paymentIntent } = await this.stripe.confirmPayment({
        elements: this.elements,
        redirect: 'if_required',
      })

      this.confirming = false
      if (error) {
        this.errorMessage = error.message || 'Stripe no pudo confirmar el pago.'
        return
      }

      this.successMessage = 'Pago confirmado. Estamos validando tu compra.'
      this.$emit('completed', {
        attemptToken: this.attemptToken,
        paymentIntentId: paymentIntent && paymentIntent.id ? paymentIntent.id : '',
        status: paymentIntent && paymentIntent.status ? paymentIntent.status : 'processing',
      })
    },
  },
}
</script>

<style scoped>
.checkout-payment-embed--stripe {
  display: grid;
  gap: 18px;
  padding: 24px;
  border-radius: 28px;
  border: 1px solid rgba(198, 205, 220, 0.1);
  background:
    radial-gradient(circle at top right, rgba(135, 145, 168, 0.12), transparent 26%),
    linear-gradient(180deg, rgba(18, 21, 32, 0.98), rgba(11, 13, 20, 0.98));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.checkout-payment-embed__hero {
  display: grid;
  gap: 14px;
}

.checkout-payment-embed__title {
  margin: 8px 0 10px;
  color: #f7f9fc;
  font-size: clamp(1.2rem, 2vw, 1.6rem);
}

.checkout-payment-embed__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.checkout-payment-embed__chips span {
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(198, 205, 220, 0.12);
  color: #dde5f3;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.checkout-payment-embed__copy,
.checkout-payment-embed__loading {
  color: rgba(207, 215, 230, 0.82);
  margin: 0;
}

.checkout-payment-embed__loading {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(198, 205, 220, 0.09);
}

.checkout-payment-embed__loading strong,
.checkout-payment-embed__hint {
  color: #eef2f7;
}

.checkout-payment-embed__loading small {
  display: block;
  color: rgba(186, 194, 208, 0.72);
}

.checkout-payment-embed__loading-ring {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.22);
  border-top-color: #f8fafc;
  animation: stripe-spin 0.9s linear infinite;
}

.checkout-payment-embed__mount {
  min-height: 140px;
  padding: 18px;
  border-radius: 22px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.01)),
    rgba(4, 6, 12, 0.38);
  border: 1px solid rgba(198, 205, 220, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.checkout-payment-embed__hint {
  margin: -4px 0 0;
  font-size: 0.92rem;
  line-height: 1.5;
}

.checkout-payment-embed__actions {
  padding-top: 4px;
}

.checkout-payment-embed__button {
  min-width: min(100%, 320px);
  border: 0;
}

@keyframes stripe-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .checkout-payment-embed--stripe {
    padding: 18px;
    border-radius: 24px;
  }

  .checkout-payment-embed__button {
    width: 100%;
  }
}
</style>
